import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useShortUrl } from './hooks';
import { ParticipationSource } from './participation';
import { queryReplace, queryCreate, queryGet, queryUpdate } from './fetch';
import { Participant, useParticipant } from './participant';
import { Session } from './session';

export interface SessionLink {
  id: string;
  sessionId: string;
  defaultSource: ParticipationSource;
  defaultSourceDetails?: string;
  recruitmentByTandemz: boolean;
  defaultLink: boolean;
  redirectionLinksId?: string;
  incentiveText?: string;
}

export interface RedirectionLinksData {
  finished?: string;
  reported?: string;
  completed?: string;
  applied?: string;
  declined?: string;
  duplicate?: string;
  speeder?: string;
  geoip?: string;
  full?: string;
  notInterested?: string;
  quotaFull?: string;
  wrongDevice?: string;
}

export interface RedirectionLinks extends RedirectionLinksData {
  id: string;
}

export const useSessionLinks = (sessionId?: string) => {
  return useQuery<SessionLink[]>(
    ['sessions', sessionId, 'session-links'],
    queryGet(`/sessions/${sessionId}/sessionLinks`),
    {
      retry: false,
      enabled: !!sessionId,
    },
  );
};

export const useSessionLink = (sessionLinkId?: string) => {
  return useQuery<SessionLink>(
    ['session-links', sessionLinkId],
    queryGet(`/session-links/${sessionLinkId}`),
    {
      retry: false,
      enabled: !!sessionLinkId,
    },
  );
};
export const useUpdateSessionLink = (sessionLinkId?: string) => {
  const queryClient = useQueryClient();
  return useMutation<SessionLink, undefined, Partial<SessionLink>>(
    queryUpdate(`/session-links/${sessionLinkId}`),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries([
          'sessions',
          data.sessionId,
          'session-links',
        ]);
        queryClient.setQueryData(['session-links', sessionLinkId], data);
      },
    },
  );
};

export const useRedirectionLinks = (sessionLinkId?: string) => {
  return useQuery<RedirectionLinks>(
    ['session-links', sessionLinkId, 'redirection-links'],
    queryGet(`/session-links/${sessionLinkId}/redirection-links`),
    {
      retry: false,
      enabled: !!sessionLinkId,
    },
  );
};

export const useHasRedirectionLinks = (sessionLinkId?: string) => {
  return useQuery<{ application: boolean; completion: boolean }>(
    ['session-links', sessionLinkId, 'has-redirection-links'],
    queryGet(`/session-links/${sessionLinkId}/has-redirection-links`),
    {
      enabled: !!sessionLinkId,
    },
  );
};

export const useUpdateRedirectionLinks = (
  sessionLinkId?: string,
  sessionId?: string,
) => {
  const queryClient = useQueryClient();
  return useMutation<RedirectionLinks, undefined, RedirectionLinksData>(
    queryReplace(`/session-links/${sessionLinkId}/redirection-links`),
    {
      onSuccess: (res) => {
        queryClient.setQueryData(
          ['sessions', sessionId, 'redirection-links'],
          res,
        );
        queryClient.setQueryData(
          ['session-links', sessionLinkId, 'redirection-links'],
          res,
        );
      },
    },
  );
};

export const useParticipantReferralLink = (
  participantId?: string | Participant,
  sessionId?: string | Session,
) => {
  const sessionIdString =
    typeof sessionId === 'string' ? sessionId : sessionId?.id;
  const { data: sessionLinks } = useSessionLinks(
    participantId ? sessionIdString : undefined,
  );
  const shortUrl = useShortUrl();

  let sessionLink = sessionLinks?.find(
    (l) =>
      l.recruitmentByTandemz &&
      l.defaultSource === ParticipationSource.REFERRAL,
  );
  if (!sessionLink) {
    sessionLink = sessionLinks?.find(
      (item) => item.recruitmentByTandemz && item.defaultLink,
    );
  }

  const { data } = useParticipant(
    typeof participantId === 'string' ? participantId : undefined,
  );

  const participant = typeof participantId === 'string' ? data : participantId;

  if (!participant || !sessionLink || !participantId || !sessionId) {
    return undefined;
  }

  return `${shortUrl}/study/${sessionLink.id}?ref=${participant.referralId}`;
};

export const useCreateSessionLink = (sessionId?: string) => {
  const queryClient = useQueryClient();
  return useMutation<SessionLink, undefined, any | undefined>(
    () =>
      queryCreate(`/sessions/${sessionId}/sessionLinks`)({
        defaultSource: ParticipationSource.PARTNER,
        recruitmentByTandemz: true,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['sessions', sessionId, 'session-links']);
      },
    },
  );
};

export const useSetSessionLinkAsDefault = (sessionLinkId: string) => {
  const queryClient = useQueryClient();
  return useMutation<SessionLink, undefined, any | undefined>(
    queryUpdate(`/session-links/${sessionLinkId}/default`),
    {
      onSuccess: ({ sessionId }) => {
        queryClient.invalidateQueries(['sessions', sessionId, 'session-links']);
      },
    },
  );
};
